import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppNotification } from '../models/app-notification.model';

const ENABLED_KEY = 'desktopNotifications';
const FIRED_KEY = 'desktop_notifications_fired';
const ICON_URL = '/assets/favicon/EZ@work.logo.2.0.192X192.png';

@Injectable({ providedIn: 'root' })
export class DesktopNotificationService {
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private firedIds = new Set<string>(
    JSON.parse(localStorage.getItem(FIRED_KEY) || '[]')
  );

  get isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  get permission(): NotificationPermission | 'unsupported' {
    return this.isSupported ? Notification.permission : 'unsupported';
  }

  private get isEnabled(): boolean {
    return this.isSupported
      && Notification.permission === 'granted'
      && localStorage.getItem(ENABLED_KEY) === 'true';
  }

  /** Requests permission (only prompts if not already decided). Resolves true if granted. */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const result = await Notification.requestPermission();
    return result === 'granted';
  }

  /** Fires a desktop notification for every unread item not already notified, deduped by id. */
  notifyNew(items: AppNotification[]): void {
    if (!this.isEnabled) return;

    const fresh = items.filter(n => !n.isRead && !this.firedIds.has(n.id));
    if (!fresh.length) return;

    fresh.forEach(n => this.fire(n));
    this.persistFiredIds();
  }

  private fire(notification: AppNotification): void {
    this.firedIds.add(notification.id);

    const title = this.translate.instant(notification.titleKey);
    const body = this.translate.instant(notification.messageKey, notification.messageParams);

    try {
      const desktopNotification = new Notification(title, {
        body,
        icon: ICON_URL,
        tag: notification.id
      });
      desktopNotification.onclick = () => {
        window.focus();
        if (notification.link) {
          this.router.navigate([notification.link]);
        }
        desktopNotification.close();
      };
    } catch {
      // Some browsers (e.g. mobile Chrome) throw on `new Notification(...)` and require a
      // Service Worker instead — silently skip rather than surfacing an error to the user.
    }
  }

  private persistFiredIds(): void {
    localStorage.setItem(FIRED_KEY, JSON.stringify([...this.firedIds]));
  }
}
